import React, { useState, useEffect, ReactElement, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '../../components/blocks/Container';
import styled from '@emotion/styled';
import { removeLocalItem, setLocalItem, toaster } from '../../common';
import BillingPackagesVariant from '../../modules/billing/BillingPackagesVariant';
import find from 'lodash/find';
import filter from 'lodash/filter';
import reject from 'lodash/reject';
import { getTextColor } from '../../style';
import BillingCarousel from '../../components/common/BillingCarousel';
import queryString from 'query-string';
import BillingPackageHeader from '../../modules/billing/BillingPackageHeader';
import './BillingPaymentStyles.css';
import { IPackages } from '../../types/common';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import breakpoints from '../../theme/material/breakpoints';

const PackageContainer = styled('div')({
  margin: '0px auto',
  padding: '5px 0 5px 0',
  textAlign: 'center',
});

const BottomMessage = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  [breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
});

const PromoAppliedIndicator = styled('div')({
  background: 'linear-gradient(135deg, #89d40e 0%, #24a83a 100%)',
  display: 'flex',
  padding: '8px 0px',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'stretch',
  borderRadius: 25,
  color: '#fff',
  textAlign: 'center',
  fontSize: 17,
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: -0.17,
  marginBottom: 40,
  fontStyle: 'italic',
  maxWidth: '100%',
  marginLeft: 'unset',
  marginRight: 'unset',
  [breakpoints.down('sm')]: {
    maxWidth: 546,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  [breakpoints.down('xs')]: {
    marginBottom: 38,
  },
});

const VatContainer = styled('div')(() => ({
  margin: '0 auto',
  marginTop: 20,
  fontSize: 13,
  ...getTextColor().DARK,
  textAlign: 'center',
}));

interface PackagesProps {
  showPayment: boolean;
  defaultPackageId: string | undefined | null;
  packages: IPackages[];
  promoCode: string | null;
  stackedVariant: boolean;
  featureVariant: any;
  promoCodeFromUrlParam: string | string[] | null;
}

const Packages = ({
  showPayment,
  defaultPackageId,
  packages,
  promoCode,
  stackedVariant,
  promoCodeFromUrlParam,
}: PackagesProps): ReactElement => {
  const { isMobile, isMobileXs } = useMediaBreakpoints();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [use2payPackageId, setUse2PayPackageId] = useState('');
  const [emailPromo, setEmailPromo] = useState(false);
  const queryStringParsed = queryString.parse(location.search);
  const unlimited = queryStringParsed.unlimitedAccess === 'true';
  const memoizedCarousel = useMemo(
    () => <BillingCarousel activeSlideIndex={unlimited ? 1 : 0} hideArrows clickableIndicators />,
    [unlimited]
  );

  const mySelectedPackage =
    find(packages, { identifier: use2payPackageId || defaultPackageId }) || packages[1] || packages[0];

  // Set types of packages
  const promoPackages = filter(packages, { promotional: true, email_promo: !!emailPromo }) || null;
  const normalPackages = filter(packages, { promotional: false, email_promo: !!emailPromo }) || null;

  // Set when promo applied using the url params "promo"
  const normalPackagePromoApplied = find(normalPackages, 'discount') || null;

  const renderPromoAppliedDiscountAmount = useMemo(() => {
    if (!normalPackagePromoApplied) return null;

    let discountAmount = '';
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    const { currency, discount } = normalPackagePromoApplied;

    switch (discount.type) {
      case 'fixed':
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        discountAmount = `${String.fromCharCode(currency.data.label_unicode)}${discount.discount} `;
        break;
      case 'percentage':
        discountAmount = (discount.discount * 100).toFixed(0) + '%';
        break;
    }

    return `${discountAmount} OFF Promo Applied!`;
  }, [normalPackagePromoApplied]);

  // Sets promo countdown variables
  const promoCountdownPackages = reject(promoPackages, { expires_at: null });
  const promoCountdownExpiresAt = promoCountdownPackages.length > 0 ? promoCountdownPackages[0].expires_at : null;
  const isPromo = promoCountdownPackages.length > 0 ? promoCountdownPackages : promoPackages;

  const routeChange = useCallback(() => {
    const path = '/packages';

    navigate(path);
  }, [navigate]);

  // Links to payment with conditions
  const packageIdNotExist =
    defaultPackageId &&
    use2payPackageId &&
    find(packages, { identifier: use2payPackageId || defaultPackageId }) === undefined;

  useEffect(() => {
    if (promoCodeFromUrlParam && !normalPackagePromoApplied) {
      navigate('/packages');
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      normalPackages.forEach(item => delete item.discount);
      toaster.warn('Promo package is no longer valid.');
    }
  }, [promoCountdownExpiresAt, promoCodeFromUrlParam, normalPackagePromoApplied]);

  useEffect(() => {
    if (showPayment && !packageIdNotExist) {
      // Redirects when mySelectedPackage is found
      // Otherwise, directs to packages page

      if (mySelectedPackage) {
        handleShowPayment(mySelectedPackage?.identifier, true);
      }
    }

    if (showPayment && defaultPackageId === 'idv_last_minute') {
      routeChange();
      handleShowPayment(defaultPackageId, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPayment, defaultPackageId, mySelectedPackage, packageIdNotExist, routeChange]);

  useEffect(() => {
    setSelectedPackage();
  }, [searchParams, defaultPackageId]);

  const setSelectedPackage = useCallback(() => {
    if (!searchParams) {
      return;
    }

    const selectedPackage = searchParams.get('selectedPackage');
    const isEmailPromo = searchParams.get('isEmailPromo');

    setEmailPromo(isEmailPromo === 'true');
    setUse2PayPackageId(selectedPackage || defaultPackageId);
  }, [defaultPackageId, searchParams]);

  const handleSelectPackage = identifier => {
    setLocalItem('wyp_selected_package', identifier);
    setUse2PayPackageId(identifier);
  };

  const handleShowPayment = useCallback(
    (packageId, isEmailPromo = false) => {
      const defaultPkg = find(packages, { name: '500 CREDIT PACKAGE' });
      let promoCodeId = '';

      setLocalItem('wyp_billing_packages', JSON.stringify(packages));

      if (isEmailPromo && promoCode !== null) {
        promoCodeId = `&promoCode=${promoCode}`;
      }

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const isPromoApplied = find(normalPackages, { identifier: packageId })?.discount;

      //  need to check if this promo applied to the selected package
      if (promoCodeFromUrlParam && isPromoApplied) {
        promoCodeId = `&promoCode=${promoCodeFromUrlParam}`;
      }

      const selectedPackage = defaultPackageId === 'idv_last_minute' ? defaultPackageId : packageId;
      const isFromIDVPackage = defaultPackageId === 'idv_last_minute' ? '&isFromIDVPackage=true' : '';

      navigate(
        `/payment?isEmailPromo=${isEmailPromo}&selectedPackageId=${
          selectedPackage ? selectedPackage : defaultPkg.identifier
        }${promoCodeId}${isFromIDVPackage}`
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultPackageId, packages, promoCode, promoCodeFromUrlParam, normalPackages]
  );

  const handleBackBtn = () => {
    removeLocalItem('wyp_billing_packages');
    navigate(-1);
  };

  const renderBillingVariants = stackedVariant => {
    return (
      <BillingPackagesVariant
        packages={normalPackages}
        selected={mySelectedPackage?.identifier}
        selectPackage={handleSelectPackage}
        promoPackages={isPromo}
        stepBilling
        emailPromo={emailPromo}
        showPayment={handleShowPayment}
        selectionOff={false}
        selectedPackage={use2payPackageId}
        stackedVariant={stackedVariant}
        normalPromoBanner={
          (promoCountdownExpiresAt || promoPackages.length > 0) &&
          normalPackagePromoApplied && (
            <PromoAppliedIndicator style={{ marginTop: isMobileXs ? 38 : 40 }}>
              {renderPromoAppliedDiscountAmount}
            </PromoAppliedIndicator>
          )
        }
        promoCountdownExpiresAt={promoCountdownExpiresAt}
      />
    );
  };

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <Container size="MD" style={{ marginBottom: '20px' }}>
      {memoizedCarousel}
      {!isMobile ? (
        <BillingPackageHeader label={`Choose Your Credit Package`} />
      ) : (
        <BillingPackageHeader label={``} onBackClick={handleBackBtn} />
      )}
      {!promoCountdownExpiresAt && normalPackagePromoApplied && promoPackages.length === 0 && (
        <PromoAppliedIndicator>{renderPromoAppliedDiscountAmount}</PromoAppliedIndicator>
      )}
      <PackageContainer>{renderBillingVariants(stackedVariant)}</PackageContainer>
      <VatContainer>
        <BottomMessage>
          <span> One-time purchase. </span> <span> All prices exclude applicable taxes.</span>
        </BottomMessage>
      </VatContainer>
    </Container>
  );
};

export default Packages;
